const axios = require('axios');
// const ErrorHandler = require('../../utilities/ErrorHandlers/ErrorHandler');

const CD_SIGNATURE = 0x02014b50;
// const EOCDR_SIGNATURE = 0x06054b50;

/**
 * Takes a remote link to a TDF and allows you to
 * download it in chunks, and get metadata about it by
 * selectively downloading its manifest.
 * @param {String} remoteLink        The URL to the file
 * @param {Object} customHeaders     Key value (header: header value) for custom headers you might need to succesfully retrieve a file.
 *                                   Most often they will authenticatio headers of some kind.
 */
function RemoteTdfNav(remoteLink, customHeaders) {
  this._remoteLink = remoteLink;
  this._customHeaders = customHeaders;

  // cache for subsequent use
  this.cdBuffers = null;
}
/**
 * Utility function to get the centralDirectory for the zip file.
 * @param  {Buffer} chunkBuffer Takes a buffer of a portion of the file
 * @return {Object}             The central directory represented as an object
 */
RemoteTdfNav.prototype.getCentralDirectory = async function () {
  const chunk = await this.getChunk(-2000);
  // Full buffer for the file chunk
  const chunkBuffer = Buffer.from(chunk);
  // Slice off the EOCDR (End of Central Directory Record) part of the buffer so we can figure out the CD size
  const cdBuffers = this.getCDBuffers(chunkBuffer);
  return cdBuffers.map(this.parseCDBuffer.bind(this));
};

/**
 * Gets the manifest
 * @returns {XMLDocument} The manifest represented as XML
 */
/* istanbul ignore next */
RemoteTdfNav.prototype.getManifest = async function (cdBuffers, fileName) {
  const cdObj = cdBuffers.filter((cdBuffer) => cdBuffer.fileName === fileName)[0];

  if (!cdObj) {
    throw new Error('Unable to retrieve CD');
  }
  const byteStart = cdObj.relativeOffsetOfLocalHeader + cdObj.headerLength;
  // TODO: what's the exact byte start?
  const byteEnd = byteStart + cdObj.uncompressedSize - 1;

  const manifest = await this.getChunk(`${byteStart}-${byteEnd}`);

  const manifestBuffer = Buffer.from(manifest);
  // match JSON? //.match(jsonRegex);
  const jsonRegex = /\{.*\:\{.*\:.*\}\}/g;

  return JSON.parse(manifestBuffer.toString());
};

RemoteTdfNav.prototype.getPayloadSegment = async function (
  cdBuffers,
  fileName,
  encrpytedSegmentOffset,
  encryptedSegmentSize
) {
  const cdObj = cdBuffers.filter((cdBuffer) => cdBuffer.fileName === fileName)[0];

  if (!cdObj) {
    throw new Error('Unable to retrieve CD');
  }
  const byteStart = cdObj.relativeOffsetOfLocalHeader + cdObj.headerLength + encrpytedSegmentOffset;
  // TODO: what's the exact byte start?
  const byteEnd = byteStart + encryptedSegmentSize - 1;

  const chunk = await this.getChunk(`${byteStart}-${byteEnd}`);
  const segmentBuffer = Buffer.from(chunk);

  return segmentBuffer;
};

/**
 * Downloads a portion of a remotely stored file
 * @param  {String} byteRange  The byte range we want to fetch. To learn more about the Range header: http://stackoverflow.com/questions/3303029/http-range-header
 * @return {Promise}           Promise that resolves to the partially fetched ZIP file.
 */
/* istanbul ignore next */
RemoteTdfNav.prototype.getChunk = function getChuck(byteRange) {
  const headers = { ...this._customHeaders };
  let lengthToRequest;
  let begin;
  let end;
  if (byteRange) {
    headers.Range = `bytes=${byteRange}`;

    if (typeof byteRange === 'string') {
      const spl = byteRange.split('-');
      begin = parseInt(spl[0] || 0);
      end = parseInt(spl[1]) + 1;
      lengthToRequest = end - begin;
    } else {
      lengthToRequest = byteRange;
    }
  }

  const opts = {
    responseType: 'arraybuffer',
    headers,
  };

  return axios.get(this._remoteLink, opts).then(function (response) {
    const responseLen = response.data.byteLength;

    // Currently Firefox is unable to honor Range headers, so this hack gets around that
    if (responseLen > lengthToRequest) {
      if (lengthToRequest > 0) return response.data.slice(begin, end);
      return response.data.slice(lengthToRequest);
    }

    return response.data;
  });
};

/**
 * Takes a portion of a ZIP (must be the last portion of a ZIP to work) and returns an array of Buffers
 * that correspond to each central directory.
 * @param  {Buffer} chunkBuffer The last portion of a zip file
 * @returns {Array}             An array of buffers
 */
RemoteTdfNav.prototype.getCDBuffers = (chunkBuffer) => {
  const cdBuffers = [];
  let lastBufferOffset = chunkBuffer.length;
  for (let i = chunkBuffer.length - 22; i >= 0; i -= 1) {
    // If what we're locking at isn't the start of a central directory, skip it..
    if (chunkBuffer.readUInt32LE(i) !== CD_SIGNATURE) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // Slice off that CD from it's start until the end of either the buffer, or whatever the start of the previously
    // found CD was
    cdBuffers.push(chunkBuffer.slice(i, lastBufferOffset));

    // Store the last offset location so we know how to slice off hte next CD.
    lastBufferOffset = i;

    // We can skip over 22 iterations since we know the minimum size of a CD is 22.
    i -= 22;
  }
  // They should be in the correct order. Since we iterate backwards, it's build backwards.
  return cdBuffers.reverse();
};

/**
 * Takes a central directory buffer and turns it into a manageable object
 * that represents the CD
 * @param  {Buffer} cdBuffer The central directory buffer to parse
 * @return {Object}          The CD object
 */
RemoteTdfNav.prototype.parseCDBuffer = function parseCDBuffer(cdBuffer) {
  if (cdBuffer.readUInt32LE(0) !== CD_SIGNATURE) {
    return new Error('Invalid central directory file header signature');
  }

  const cd = {};

  // 4 - Version made by
  cd.versionMadeBy = cdBuffer.readUInt16LE(4);
  // 6 - Version needed to extract (minimum)
  cd.versionNeededToExtract = cdBuffer.readUInt16LE(6);
  // 8 - General purpose bit flag
  cd.generalPurposeBitFlag = cdBuffer.readUInt16LE(8);
  // 10 - Compression method
  cd.compressionMethod = cdBuffer.readUInt16LE(10);
  // 12 - File last modification time
  cd.lastModFileTime = cdBuffer.readUInt16LE(12);
  // 14 - File last modification date
  cd.lastModFileDate = cdBuffer.readUInt16LE(14);
  // 16 - CRC-32
  cd.crc32 = cdBuffer.readUInt32LE(16);
  // 20 - Compressed size
  cd.compressedSize = cdBuffer.readUInt32LE(20);
  // 24 - Uncompressed size
  cd.uncompressedSize = cdBuffer.readUInt32LE(24);
  // 28 - File name length (n)
  cd.fileNameLength = cdBuffer.readUInt16LE(28);
  // 30 - Extra field length (m)
  cd.extraFieldLength = cdBuffer.readUInt16LE(30);
  // 32 - File comment length (k)
  cd.fileCommentLength = cdBuffer.readUInt16LE(32);
  // 34 - Disk number where file starts
  // 36 - Internal file attributes
  cd.internalFileAttributes = cdBuffer.readUInt16LE(36);
  // 38 - External file attributes
  cd.externalFileAttributes = cdBuffer.readUInt32LE(38);
  // 42 - Relative offset of local file header
  cd.relativeOffsetOfLocalHeader = cdBuffer.readUInt32LE(42);

  const fileNameBuffer = cdBuffer.slice(46, 46 + cd.fileNameLength);
  // eslint-disable-next-line no-bitwise
  const isUtf8 = cd.generalPurposeBitFlag & 0x800;

  cd.fileName = bufferToString(fileNameBuffer, 0, cd.fileNameLength, isUtf8);

  cd.headerLength = 30 + cd.fileNameLength;

  return cd;
};

// eslint-disable-next-line max-len
const cp437 =
  '\u0000☺☻♥♦♣♠•◘○◙♂♀♪♫☼►◄↕‼¶§▬↨↑↓→←∟↔▲▼ !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~⌂ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';
/**
 * Takes a buffer, and turns it into a string
 * @param  {Buffer}  buffer The buffer to convert
 * @param  {Number}  start  The start location of the part of the buffer to convert
 * @param  {Number}  end    The end location of the part of the buffer to convert
 * @param  {Boolean} isUtf8 Is it utf8?
 * @return {String}         The converted string
 */
function bufferToString(buffer, start, end, isUtf8) {
  if (isUtf8) {
    return buffer.toString('utf8', start, end);
  }

  let result = '';
  for (let i = start; i < end; i++) {
    if (cp437[buffer[i]]) {
      result += cp437[buffer[i]];
    }
  }
  return result;
}

/// /////////// WRITING A ZIP

var LOCAL_FILE_HEADER_FIXED_SIZE = 30;
var VERSION_NEEDED_TO_EXTRACT_UTF8 = 20;
var FILE_NAME_IS_UTF8 = 1 << 11;
var UNKNOWN_CRC32_AND_FILE_SIZES = 1 << 3;

RemoteTdfNav.prototype.getLocalFileHeader = function (
  utf8FileName,
  crc32,
  compressedSize,
  uncompressedSize
) {
  const fixedSizeStuff = new Buffer(LOCAL_FILE_HEADER_FIXED_SIZE);
  let generalPurposeBitFlag = FILE_NAME_IS_UTF8;

  // we can't know the size in advance
  // TODO: this means we need to add a data descriptor!
  generalPurposeBitFlag |= UNKNOWN_CRC32_AND_FILE_SIZES;

  const dateModified = dateToDosDateTime(new Date());
  const filenameBuffer = Buffer.from(utf8FileName);

  // local file header signature     4 bytes  (0x04034b50)
  fixedSizeStuff.writeUInt32LE(0x04034b50, 0);
  // version needed to extract       2 bytes
  fixedSizeStuff.writeUInt16LE(VERSION_NEEDED_TO_EXTRACT_UTF8, 4);
  // general purpose bit flag        2 bytes
  fixedSizeStuff.writeUInt16LE(generalPurposeBitFlag, 6);
  // compression method              2 bytes
  fixedSizeStuff.writeUInt16LE(NO_COMPRESSION, 8);
  // last mod file time              2 bytes
  fixedSizeStuff.writeUInt16LE(dateModified.time, 10);
  // last mod file date              2 bytes
  fixedSizeStuff.writeUInt16LE(dateModified.date, 12);
  // crc-32                          4 bytes
  fixedSizeStuff.writeUInt32LE(crc32, 14);
  // compressed size                 4 bytes
  fixedSizeStuff.writeUInt32LE(compressedSize, 18);
  // uncompressed size               4 bytes
  fixedSizeStuff.writeUInt32LE(uncompressedSize, 22);
  // file name length                2 bytes
  fixedSizeStuff.writeUInt16LE(filenameBuffer.length, 26);
  // extra field length              2 bytes
  fixedSizeStuff.writeUInt16LE(0, 28);
  return Buffer.concat([
    fixedSizeStuff,
    // file name (variable size)
    filenameBuffer,
    // extra field (variable size)
    // no extra fields
  ]);
};

/*

{ versionMadeBy: 831,
    versionNeededToExtract: 20,
    generalPurposeBitFlag: 2048,
    compressionMethod: 0,
    lastModFileTime: 29443,
    lastModFileDate: 19762,
    crc32: 3735382831,
    compressedSize: 2583,
    uncompressedSize: 2583,
    fileNameLength: 15,
    extraFieldLength: 0,
    fileCommentLength: 0,
    internalFileAttributes: 0,
    externalFileAttributes: 2176057344,
    relativeOffsetOfLocalHeader: 0,
    fileName: '0.manifest.json',
    headerLength: 45 }


*/

const CENTRAL_DIRECTORY_RECORD_FIXED_SIZE = 46;

var LOCAL_FILE_HEADER_FIXED_SIZE = 30;
var VERSION_NEEDED_TO_EXTRACT_UTF8 = 20;
const VERSION_NEEDED_TO_EXTRACT_ZIP64 = 45;
// 3 = unix. 63 = spec version 6.3
const VERSION_MADE_BY = (3 << 8) | 63;
var FILE_NAME_IS_UTF8 = 1 << 11;
var UNKNOWN_CRC32_AND_FILE_SIZES = 1 << 3;

var NO_COMPRESSION = 0;

const DATA_DESCRIPTOR_SIZE = 16;

RemoteTdfNav.prototype.writeDataDescriptor = function (crc32, uncompressedSize) {
  const buffer = new Buffer(DATA_DESCRIPTOR_SIZE);
  // optional signature (required according to Archive Utility)
  buffer.writeUInt32LE(0x08074b50, 0);
  // crc-32                          4 bytes
  buffer.writeUInt32LE(crc32, 4);
  // compressed size                 4 bytes
  buffer.writeUInt32LE(uncompressedSize, 8);
  // uncompressed size               4 bytes
  buffer.writeUInt32LE(uncompressedSize, 12);

  return buffer;
};

// var crc32 = require("buffer-crc32");

RemoteTdfNav.prototype.writeCentralDirectoryRecord = function (
  uncompressedSize,
  utf8FileName,
  normalRelativeOffsetOfLocalHeader,
  crc32,
  externalFileAttributes
) {
  const fixedSizeStuff = new Buffer(CENTRAL_DIRECTORY_RECORD_FIXED_SIZE);
  let generalPurposeBitFlag = FILE_NAME_IS_UTF8;

  // we can't know the size in advance
  // TODO: this means we need to add a data descriptor!
  generalPurposeBitFlag |= UNKNOWN_CRC32_AND_FILE_SIZES;

  const zeiefBuffer = new Buffer(0);

  const dateModified = dateToDosDateTime(new Date());
  const filenameBuffer = Buffer.from(utf8FileName);

  // central file header signature   4 bytes  (0x02014b50)
  fixedSizeStuff.writeUInt32LE(CD_SIGNATURE, 0);
  // version made by                 2 bytes
  fixedSizeStuff.writeUInt16LE(VERSION_MADE_BY, 4);
  // version needed to extract       2 bytes
  fixedSizeStuff.writeUInt16LE(VERSION_NEEDED_TO_EXTRACT_UTF8, 6);
  // general purpose bit flag        2 bytes
  fixedSizeStuff.writeUInt16LE(generalPurposeBitFlag, 8);
  // compression method              2 bytes
  fixedSizeStuff.writeUInt16LE(NO_COMPRESSION, 10);
  // last mod file time              2 bytes
  fixedSizeStuff.writeUInt16LE(dateModified.time, 12);
  // last mod file date              2 bytes
  fixedSizeStuff.writeUInt16LE(dateModified.date, 14);
  // crc-32                          4 bytes
  fixedSizeStuff.writeUInt32LE(crc32, 16);
  // compressed size                 4 bytes
  fixedSizeStuff.writeUInt32LE(uncompressedSize, 20);
  // uncompressed size               4 bytes
  fixedSizeStuff.writeUInt32LE(uncompressedSize, 24);
  // file name length                2 bytes
  fixedSizeStuff.writeUInt16LE(filenameBuffer.length, 28);
  // extra field length              2 bytes
  fixedSizeStuff.writeUInt16LE(zeiefBuffer.length, 30);
  // file comment length             2 bytes
  fixedSizeStuff.writeUInt16LE(0, 32);
  // disk number start               2 bytes
  fixedSizeStuff.writeUInt16LE(0, 34);
  // internal file attributes        2 bytes
  fixedSizeStuff.writeUInt16LE(0, 36);
  // external file attributes        4 bytes
  fixedSizeStuff.writeUInt32LE(externalFileAttributes, 38);
  // relative offset of local header 4 bytes
  fixedSizeStuff.writeUInt32LE(normalRelativeOffsetOfLocalHeader, 42);

  return Buffer.concat([
    fixedSizeStuff,
    // file name (variable size)
    filenameBuffer,
    // extra field (variable size)
    zeiefBuffer,
    // file comment (variable size)
    // empty comment
  ]);
};

function dateToDosDateTime(jsDate) {
  let date = 0;
  date |= jsDate.getDate() & 0x1f; // 1-31
  date |= ((jsDate.getMonth() + 1) & 0xf) << 5; // 0-11, 1-12
  date |= ((jsDate.getFullYear() - 1980) & 0x7f) << 9; // 0-128, 1980-2108

  let time = 0;
  time |= Math.floor(jsDate.getSeconds() / 2); // 0-59, 0-29 (lose odd numbers)
  time |= (jsDate.getMinutes() & 0x3f) << 5; // 0-59
  time |= (jsDate.getHours() & 0x1f) << 11; // 0-23

  return { date, time };
}

const END_OF_CENTRAL_DIRECTORY_RECORD_SIZE = 22;

RemoteTdfNav.prototype.writeEndOfCentralDirectoryRecord = function (
  normalEntriesLength,
  normalSizeOfCentralDirectory,
  normalOffsetOfStartOfCentralDirectory
) {
  const eocdrBuffer = new Buffer(END_OF_CENTRAL_DIRECTORY_RECORD_SIZE);
  // end of central dir signature                       4 bytes  (0x06054b50)
  eocdrBuffer.writeUInt32LE(0x06054b50, 0);
  // number of this disk                                2 bytes
  eocdrBuffer.writeUInt16LE(0, 4);
  // number of the disk with the start of the central directory  2 bytes
  eocdrBuffer.writeUInt16LE(0, 6);
  // total number of entries in the central directory on this disk  2 bytes
  eocdrBuffer.writeUInt16LE(normalEntriesLength, 8);
  // total number of entries in the central directory   2 bytes
  eocdrBuffer.writeUInt16LE(normalEntriesLength, 10);
  // size of the central directory                      4 bytes
  eocdrBuffer.writeUInt32LE(normalSizeOfCentralDirectory, 12);
  // offset of start of central directory with respect to the starting disk number  4 bytes
  eocdrBuffer.writeUInt32LE(normalOffsetOfStartOfCentralDirectory, 16);
  // .ZIP file comment length                           2 bytes
  eocdrBuffer.writeUInt16LE(0, 20);
  // .ZIP file comment                                  (variable size)
  // no comment

  return eocdrBuffer;
};

module.exports = RemoteTdfNav;
